/**
 * Personal records (step 4): pure helpers, no DOM, no network.
 *
 * Canonical units after the adapter: kg for weight kinds, seconds for duration,
 * meters for distance. Display goes through the step-1 formatters.
 */
import { getLocale, t, type MessageKey } from "../i18n";
import { formatDistance, formatSeconds } from "./activity";

export type RecordKind = "maxWeight" | "estOneRepMax" | "maxDuration" | "maxDistance";

/** Display order of kinds in the records block. */
export const RECORD_KINDS: readonly RecordKind[] = [
  "maxWeight",
  "estOneRepMax",
  "maxDuration",
  "maxDistance",
] as const;

export function isRecordKind(v: unknown): v is RecordKind {
  return typeof v === "string" && (RECORD_KINDS as readonly string[]).includes(v);
}

export interface PersonalRecord {
  kind: RecordKind;
  /** kg | seconds | meters, by kind. */
  value: number;
  /** ISO date (YYYY-MM-DD) or null when the backend did not send one. */
  date: string | null;
  workoutId: string | null;
}

const LABEL: Record<RecordKind, MessageKey> = {
  maxWeight: "REC_MAX_WEIGHT",
  estOneRepMax: "REC_EST_1RM",
  maxDuration: "REC_MAX_DURATION",
  maxDistance: "REC_MAX_DISTANCE",
};

/** Lower-case label ("макс. вес"), used in the badge. */
export function recordKindLabel(kind: RecordKind): string {
  return t(LABEL[kind]);
}

/** Sentence-case label ("Макс. вес"), used in the records list. */
export function recordKindTitle(kind: RecordKind): string {
  const s = recordKindLabel(kind);
  return s.charAt(0).toLocaleUpperCase(getLocale()) + s.slice(1);
}

/** Kinds in display order, unknown values and duplicates dropped. */
export function normalizeKinds(raw: unknown): RecordKind[] {
  if (!Array.isArray(raw)) return [];
  return RECORD_KINDS.filter((k) => raw.includes(k));
}

/** Convert a value with an optional unit to the canonical unit of its kind; null = unusable. */
export function toCanonical(kind: RecordKind, value: number, unit?: unknown): number | null {
  if (!Number.isFinite(value) || value <= 0) return null;
  const u = typeof unit === "string" ? unit.trim().toLowerCase() : "";
  let factor: number | null = null;
  switch (kind) {
    case "maxWeight":
    case "estOneRepMax":
      factor = ({ "": 1, kg: 1, кг: 1, g: 0.001, lb: 0.45359237, lbs: 0.45359237 } as Record<string, number>)[u] ?? null;
      break;
    case "maxDuration":
      factor =
        ({ "": 1, s: 1, sec: 1, seconds: 1, сек: 1, min: 60, minutes: 60, мин: 60, h: 3600, hours: 3600 } as Record<string, number>)[u] ??
        null;
      break;
    case "maxDistance":
      factor = ({ "": 1, m: 1, meters: 1, м: 1, km: 1000, км: 1000 } as Record<string, number>)[u] ?? null;
      break;
  }
  return factor == null ? null : value * factor;
}

/** "62.5 кг", "~80 кг" (estimate), "34 мин 40 сек", "1500 м". */
export function formatRecordValue(kind: RecordKind, value: number): string {
  switch (kind) {
    case "maxWeight":
    case "estOneRepMax": {
      const n = Math.round(value * 10) / 10;
      return `${kind === "estOneRepMax" ? "~" : ""}${n} ${t("KG")}`;
    }
    case "maxDuration":
      return formatSeconds(value);
    case "maxDistance":
      return formatDistance(value);
  }
}

/** "5 окт" in the current year, "5 окт 2025" otherwise; "" when the date is missing or invalid. */
export function formatRecordDate(date: string | null | undefined, now: Date = new Date()): string {
  const m = typeof date === "string" ? date.match(/^(\d{4})-(\d{2})-(\d{2})/) : null;
  if (!m) return "";
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString(getLocale() === "en" ? "en-US" : "ru-RU", {
    day: "numeric",
    month: "short",
    ...(d.getFullYear() === now.getFullYear() ? {} : { year: "numeric" }),
  });
}

/** Badge text parts: kind labels of the beaten records, e.g. "макс. вес, расч. 1ПМ". */
export function newRecordLabels(kinds: readonly RecordKind[] | undefined): string {
  return (kinds ?? []).map(recordKindLabel).join(", ");
}
